import React, { useEffect, useState, useRef } from "react";
import { Pagination } from "antd"; // Import Ant Design Pagination
import "./Notification.scss";
import axios from "axios";
import { baseUrl } from "../../utils/apis";
import { useDispatch, useSelector } from "react-redux";
import { setUnreadCount } from "../../store/reducers/notificationSlice";
import { stripHtml } from "../../utils/helpers/string";

const Notification = () => {
  const dispatch = useDispatch();
  const unreadCount = useSelector((state) => state.notification.unreadCount);
  const idToken = useSelector((state) => state.auth.idToken);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const [selectedId, setSelectedId] = useState(null);
  const isDetailLoading = false;
  const contentRef = useRef(null);

  // Fetch notifications list
  const fetchNotifications = async (page = 1) => {
    setIsLoading(true);
    try {
      const config = {
        headers: { Authorization: `Bearer ${idToken}` },
        params: { page, page_size: 10 },
      };
      const response = await axios.get(`${baseUrl}/auth/notifications/`, config);
      if (response.data) {
        setMessages(response.data.results || response.data.data || response.data || []);
        setTotalCount(response.data.count || response.data.totalCount || 0);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch unread count
  const fetchUnreadCount = async () => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${idToken}` },
      };
      const response = await axios.get(`${baseUrl}/auth/notifications/unread-count/`, config);
      if (response.data) {
        dispatch(setUnreadCount(response.data.count || 0));
      }
    } catch (error) {
      console.error("Error fetching unread count:", error);
    }
  };

  // Initial load
  useEffect(() => {
    if (idToken) {
      fetchNotifications(currentPage);
      fetchUnreadCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToken, currentPage]);

  // Mark a single notification as read
  const markAsRead = async (id) => {
    try {
      const config = {
        headers: { Authorization: `Bearer ${idToken}` },
      };
      await axios.patch(`${baseUrl}/auth/notifications/${id}/read/`, {}, config);
      // Update local state to show it's read
      setMessages((prev) => prev.map((msg) => (msg.id === id ? { ...msg, is_read: true } : msg)));
      // Re-fetch unread count to keep it accurate
      fetchUnreadCount();
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  // Reset selection when the page changes
  useEffect(() => {
    if (messages.length > 0) {
      setSelectedId(messages[0].id);
      if (!messages[0].is_read) {
        markAsRead(messages[0].id);
      }
    } else {
      setSelectedId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  const handleSelectMessage = (msg) => {
    const msgId = msg.id;
    setSelectedId(msgId);
    if (!msg.is_read) {
      markAsRead(msgId);
    }

    // Scroll to the content on mobile sizes
    if (window.innerWidth <= 768 && contentRef.current) {
      // Small timeout allows the DOM to render the selected message view before scrolling
      setTimeout(() => {
        contentRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    }
  };

  const onPageChange = (page) => {
    setCurrentPage(page);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const selectedMessagePreview = messages.find((m) => m.id === selectedId);

  return (
    <div className="inbox-wrapper">
      {/* ------------------ Left Side: Message List ------------------ */}
      <div className="inbox-sidebar-container">
        <div className="inbox-sidebar">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }} className="inbox-sidebar-wrapper">
            <h3
              className="sidebar-header"
              style={{ margin: 0 }}
            >
              All Messages
            </h3>
            {unreadCount > 0 && <span className="unread-badge">{unreadCount} Unread</span>}
          </div>

          <div className="message-list">
            {messages.map((msg) => {
              const msgId = msg.id;
              return (
                <div
                  key={msgId}
                  className={`message-item ${selectedId === msgId ? "active" : ""}`}
                  onClick={() => handleSelectMessage(msg)}
                >
                  <div className="msg-header-row">
                    <span className="msg-title">{msg.subject}</span>
                    {!msg.is_read && <span className="unread-dot"></span>}
                  </div>
                  <p className="msg-preview">{stripHtml(msg.body || "").substring(0, 50) || "No preview..."}</p>
                  <span className="msg-date">{formatDate(msg.created_at)}</span>
                </div>
              );
            })}

            {!isLoading && messages.length === 0 && <div className="empty-state">No messages found.</div>}
          </div>

          {/* Pagination Component */}
          {totalCount > 10 && (
            <div className="pagination-wrapper">
              <Pagination
                current={currentPage}
                total={totalCount}
                pageSize={10}
                onChange={onPageChange}
                size="small"
                showSizeChanger={false}
              />
            </div>
          )}
        </div>
      </div>

      {/* ------------------ Right Side: Message Detail ------------------ */}
      <div className="inbox-content" ref={contentRef}>
        {selectedId && selectedMessagePreview ? (
          <div className="message-detail">
            <h2 className="detail-title">{selectedMessagePreview.subject}</h2>
            <span className="detail-date">{formatDate(selectedMessagePreview.created_at)}</span>

            <div className="detail-body">
              {isDetailLoading ? (
                <p className="loading-text">Loading content...</p>
              ) : (
                <p>{stripHtml(selectedMessagePreview?.body || "")}</p>
              )}
            </div>
          </div>
        ) : (
          <div className="empty-state">Select a message to view details</div>
        )}
      </div>
    </div>
  );
};

export default Notification;
